/**
 * HistoryModal — Search archived shifts with full personnel detail.
 * Matches the layout shown in the screenshot:
 *   - FROM/TO date pickers, shift selector, Name/Call Sign search, Equipment# search
 *   - Post / Task tab buttons
 *   - Results table: Date | Shift | Name | Call Sign | Role | Key | Radio | Recorder | Badge | Posts | Tasks
 */
import { useState } from 'react'
import { workerPost } from '../../../api/worker'
import { useAuthStore } from '../../../stores/auth'
import { modalOverlay, modalBox, darkInput } from './modalShared'
import ModalHeader from './ModalHeader'

interface PersonnelEntry {
  name?: string
  callSign?: string
  role?: string
  key?: string
  radio?: string
  recorder?: string
  badge?: string
  posts?: { post?: string; isBreak?: boolean; from?: string; to?: string }[]
}

interface SecondaryTask {
  task?: string
  callSign?: string
  detail?: string
}

interface ArchiveEntry {
  Date: string
  Shift: string
  'Archived By': string
  'Archived At': string
  syncData?: {
    fom_personnel?: string
    fom_secondaryTasks?: string
    [key: string]: string | undefined
  }
}

interface ResultRow {
  date: string
  shift: string
  name: string
  callSign: string
  role: string
  key: string
  radio: string
  recorder: string
  badge: string
  posts: string
  tasks: string
}

function sn(n: string): string {
  if (!n) return '—'
  const s = n.replace(/\s*\(.*?\)\s*/g, '').replace(/^(Dir\.|AD\.|Lt\.|Sgt\.|Ofc\.)\s*/i, '').trim()
  if (s.includes(',')) return s.split(',')[0].trim()
  const p = s.split(/\s+/)
  return p.length > 1 ? p[p.length - 1] : p[0]
}

function postAbbr(posts: PersonnelEntry['posts']): string {
  if (!posts?.length) return '—'
  const abbr: Record<string, string> = { Mall: 'M', Vehicle: 'V', Desk: 'D', Special: 'SP', Escalators: 'ESC', Detex: 'DX' }
  const set = new Set(
    posts.filter(x => !x.isBreak && x.post && x.post !== 'Break')
      .map(x => abbr[x.post!] || x.post!.charAt(0))
  )
  return Array.from(set).join(', ') || '—'
}

function flattenArchive(entries: ArchiveEntry[], nameFilter: string, eqFilter: string, tabFilter: 'all' | 'post' | 'task'): ResultRow[] {
  const rows: ResultRow[] = []
  for (const entry of entries) {
    let personnel: PersonnelEntry[] = []
    let tasks: SecondaryTask[] = []
    try {
      if (entry.syncData?.fom_personnel) personnel = JSON.parse(entry.syncData.fom_personnel)
      if (entry.syncData?.fom_secondaryTasks) tasks = JSON.parse(entry.syncData.fom_secondaryTasks)
    } catch { /* ignore parse errors */ }

    const active = personnel.filter(p => p.name)

    if (!active.length) {
      // Archive has no personnel — show the date/shift row with blanks
      rows.push({ date: entry.Date, shift: entry.Shift, name: '—', callSign: '—', role: '—', key: '—', radio: '—', recorder: '—', badge: '—', posts: '—', tasks: '—' })
      continue
    }

    for (const p of active) {
      const cs = (p.callSign || '').toUpperCase()
      const myTasks = tasks
        .filter(t => t.callSign && t.callSign.toUpperCase() === cs)
        .map(t => t.task || '')
        .filter(Boolean)
        .join(', ')

      const row: ResultRow = {
        date: entry.Date,
        shift: entry.Shift,
        name: sn(p.name || ''),
        callSign: p.callSign || '—',
        role: p.role || '—',
        key: p.key || '—',
        radio: p.radio || '—',
        recorder: p.recorder || '—',
        badge: p.badge || '—',
        posts: postAbbr(p.posts),
        tasks: myTasks || '—',
      }

      // Name/callSign filter
      if (nameFilter) {
        const f = nameFilter.toLowerCase()
        if (!row.name.toLowerCase().includes(f) && !row.callSign.toLowerCase().includes(f)) continue
      }

      // Equipment filter
      if (eqFilter) {
        const f = eqFilter.toLowerCase()
        if (![row.key, row.radio, row.recorder, row.badge].some(v => v.toLowerCase().includes(f))) continue
      }

      // Tab filter
      if (tabFilter === 'post' && row.posts === '—') continue
      if (tabFilter === 'task' && row.tasks === '—') continue

      rows.push(row)
    }
  }
  return rows
}

export default function HistoryModal({ onClose }: { onClose: () => void }) {
  const token = useAuthStore(s => s.session?.token ?? '')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo,   setDateTo]   = useState('')
  const [shiftF,   setShiftF]   = useState('')
  const [nameF,    setNameF]    = useState('')
  const [eqF,      setEqF]      = useState('')
  const [tab,      setTab]      = useState<'all' | 'post' | 'task'>('all')
  const [loading,  setLoading]  = useState(false)
  const [raw,      setRaw]      = useState<ArchiveEntry[]>([])
  const [searched, setSearched] = useState(false)

  async function doSearch() {
    setLoading(true)
    setSearched(true)
    try {
      const res = await workerPost<{ success: boolean; results?: ArchiveEntry[] }>({
        authAction: 'searchHistory',
        token,
        dateFrom: dateFrom || undefined,
        dateTo:   dateTo   || undefined,
        shift:    shiftF   || undefined,
      })
      setRaw(res.success && res.results ? res.results : [])
    } catch {
      setRaw([])
    }
    setLoading(false)
  }

  const rows = flattenArchive(raw, nameF, eqF, tab)

  const inp: React.CSSProperties = { ...darkInput, width: 'auto', flex: '0 0 auto' }
  const tabBtn = (t: typeof tab, label: string) => (
    <button
      onClick={() => setTab(t)}
      style={{
        padding: '4px 14px',
        borderRadius: 6,
        border: 'none',
        cursor: 'pointer',
        fontWeight: 600,
        fontSize: 12,
        background: tab === t ? '#16a34a' : 'rgba(255,255,255,0.07)',
        color: tab === t ? '#fff' : 'var(--text-muted)',
        transition: 'all 0.15s',
      }}
    >{label}</button>
  )

  const th: React.CSSProperties = {
    padding: '6px 8px',
    fontSize: 11,
    fontWeight: 700,
    color: 'var(--text-muted)',
    textAlign: 'left',
    borderBottom: '1px solid rgba(255,255,255,0.08)',
    whiteSpace: 'nowrap',
    position: 'sticky',
    top: 0,
    background: '#0f1c2e',
  }
  const td: React.CSSProperties = {
    padding: '5px 8px',
    fontSize: 12,
    color: 'var(--text)',
    borderBottom: '1px solid rgba(255,255,255,0.04)',
    whiteSpace: 'nowrap',
  }

  return (
    <div style={modalOverlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ ...modalBox(900), display: 'flex', flexDirection: 'column', maxHeight: '85vh' }}>
        <ModalHeader title="🔍 Search Shift History" onClose={onClose} />

        {/* Filter row 1 */}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 6 }}>
          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>FROM:</span>
          <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} style={inp} />
          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>TO:</span>
          <input type="date" value={dateTo}   onChange={e => setDateTo(e.target.value)}   style={inp} />
          <select value={shiftF} onChange={e => setShiftF(e.target.value)} style={{ ...inp, cursor: 'pointer' }}>
            <option value="">All Shifts</option>
            <option>Morning</option>
            <option>Evening</option>
            <option>Night</option>
          </select>
          <input
            placeholder="Name or Call Sign"
            value={nameF}
            onChange={e => setNameF(e.target.value)}
            style={{ ...inp, width: 140 }}
          />
          <input
            placeholder="Equipment # (key, radio…)"
            value={eqF}
            onChange={e => setEqF(e.target.value)}
            style={{ ...inp, width: 160 }}
          />
        </div>

        {/* Filter row 2 — tabs + search button */}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 10 }}>
          {tabBtn('post', 'Post')}
          {tabBtn('task', 'Task')}
          <button
            onClick={doSearch}
            style={{
              background: '#16a34a',
              border: 'none',
              borderRadius: 8,
              padding: '6px 20px',
              color: '#fff',
              fontWeight: 700,
              cursor: 'pointer',
              fontSize: 13,
              opacity: loading ? 0.7 : 1,
            }}
          >
            {loading ? '…' : 'Search'}
          </button>
        </div>

        {/* Results */}
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {!searched ? null : loading ? (
            <div style={{ color: 'var(--text-hint)', textAlign: 'center', padding: 32, fontSize: 13 }}>Searching…</div>
          ) : rows.length === 0 ? (
            <div style={{ color: 'var(--text-hint)', textAlign: 'center', padding: 32, fontSize: 13 }}>No results found.</div>
          ) : (
            <>
              <div style={{ fontSize: 12, color: '#22c55e', fontWeight: 700, marginBottom: 6 }}>
                {rows.length} result{rows.length !== 1 ? 's' : ''} found
              </div>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 700 }}>
                  <thead>
                    <tr>
                      {['Date','Shift','Name','Call Sign','Role','Key','Radio','Recorder','Badge','Posts','Tasks'].map(h => (
                        <th key={h} style={th}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, i) => (
                      <tr key={i} style={{ background: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.015)' }}>
                        <td style={{ ...td, fontWeight: 700, color: 'var(--accent)' }}>{r.date}</td>
                        <td style={td}>{r.shift}</td>
                        <td style={td}>{r.name}</td>
                        <td style={{ ...td, color: '#94b8d8' }}>{r.callSign}</td>
                        <td style={{ ...td, fontStyle: 'italic', color: 'var(--text-muted)' }}>{r.role}</td>
                        <td style={td}>{r.key}</td>
                        <td style={td}>{r.radio}</td>
                        <td style={td}>{r.recorder}</td>
                        <td style={td}>{r.badge}</td>
                        <td style={td}>{r.posts}</td>
                        <td style={td}>{r.tasks}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
